import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import Hint from 'components/styleguide/hint/Hint';
import Icon from 'components/styleguide/icon/Icon';
import LocationInput from 'components/form/location_input/location_input';

const styles = require('./LocationField.module.scss');

type Context = 'talent' | 'company' | 'neutral';

interface LocationValue {
  city: string;
  country: string;
  countryCode: string;
}

export interface LocationFieldProps {
  autoSaveUrl?: string;
  containerClassName?: string;
  context?: Context;
  defaultValue?: LocationValue;
  disabled?: boolean;
  error?: string;
  formName?: string;
  hint?: string;
  id?: string;
  label?: string;
  labelSuffix?: React.ReactNode;
  onLocationChange?: (event: any) => void;
  onReset?: () => void;
  placeholder?: string;
  required?: boolean;
  value?: LocationValue;
}

function getCsrfToken(): string {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]');
  return meta?.content || '';
}

const LocationField: React.FC<LocationFieldProps> = ({
  autoSaveUrl,
  containerClassName,
  context = 'neutral',
  defaultValue,
  disabled,
  error,
  formName,
  hint,
  id = 'location-filter',
  label,
  labelSuffix,
  onLocationChange,
  onReset,
  placeholder = 'E.g. Lisbon',
  required,
  value,
}) => {
  const [displayError, setDisplayError] = useState(error || '');
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setDisplayError(error || '');
  }, [error]);

  const saveLocation = useCallback(() => {
    if (!autoSaveUrl || !wrapperRef.current) return;

    const hiddenInputs = wrapperRef.current.querySelectorAll<HTMLInputElement>('input[type="hidden"]');
    const formData = new FormData();
    hiddenInputs.forEach((input) => {
      if (input.name) formData.append(input.name, input.value);
    });

    fetch(autoSaveUrl, {
      method: 'PATCH',
      headers: { 'X-CSRF-Token': getCsrfToken(), 'Accept': 'application/json' },
      body: formData,
    }).then((res) => {
      if (!res.ok) res.json().then((data) => {
        const err = data.errors?.city || data.errors?.country_code;
        if (err) setDisplayError(err);
      });
    });
  }, [autoSaveUrl]);

  const handleLocationChange = useCallback((event: any) => {
    setDisplayError('');
    onLocationChange?.(event);
    setTimeout(saveLocation, 100);
  }, [onLocationChange, saveLocation]);

  const handleInput = useCallback(() => {
    setDisplayError('');
  }, []);

  // LocationInput renders a focusable div
  useEffect(() => {
    const container = wrapperRef.current?.querySelector<HTMLElement>('.location-search-container');
    container?.setAttribute('tabindex', '-1');
  }, []);

  const hasMessage = Boolean(displayError) || Boolean(hint);
  const messageId = hasMessage ? `${id}-message` : undefined;

  const locationInputClasses = classNames(
    styles.locationInput,
    styles.withLeftIcon,
    {
      [styles[context]]: context,
      [styles.error]: Boolean(displayError),
      [styles.disabled]: disabled,
    }
  );

  const iconColor = disabled ? 'neutral-text-disable' : 'neutral-border-darker';

  return (
    <div className={classNames(styles.wrapper, containerClassName)}>
      {label && (
        <div className={styles.labelContainer}>
          <label htmlFor={id} className={styles.label}>{label}</label>
          {required && <span className={styles.required} aria-hidden="true">*</span>}
          {labelSuffix}
        </div>
      )}

      <div className={styles.inputWrapper}>
        <label
          htmlFor={id}
          ref={wrapperRef as unknown as React.Ref<HTMLLabelElement>}
          onInput={handleInput}
          className={styles.controlLabel}
        >
          <span className={styles.leftIcon}>
            <Icon name="mapPin" size="sm" color={iconColor} aria-hidden="true" />
          </span>
          <LocationInput
            hideIcon={true}
            formName={formName}
            defaultValue={defaultValue}
            id={id}
            value={value}
            handleLocationChange={handleLocationChange}
            handleReset={onReset}
            placeholder={placeholder}
            required={required}
            class={locationInputClasses}
          />
        </label>

        {hasMessage && (
          <Hint
            id={messageId}
            variant={displayError ? 'error' : 'neutral'}
            message={displayError || hint}
          />
        )}
      </div>
    </div>
  );
};

export default LocationField;
